import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{message: string; is_first_user: boolean} | null>(null);

  // Redirect to dashboard if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = (): string | null => {
    if (!formData.username || !formData.email || !formData.password) {
      return 'Please fill in all required fields';
    }
    
    if (formData.username.length < 3) {
      return 'Username must be at least 3 characters';
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      return 'Please enter a valid email address';
    }
    
    if (formData.password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    
    if (formData.password !== formData.confirmPassword) {
      return 'Passwords do not match';
    }
    
    return null;
  };

  // T056: handleRegister using AuthContext
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Client-side validation
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      // Use AuthContext register function
      const response = await register(
        formData.username,
        formData.email,
        formData.password,
        formData.full_name || undefined
      );
      
      setSuccess({
        message: response.message,
        is_first_user: response.is_first_user,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      
      // Multi-tenancy (v0.13.0): the backend refuses registration with 403 once the first
      // account exists — new users arrive by invitation, which is what attaches a workspace
      // membership and consumes a seat. Say that, rather than surfacing a bare error.
      if (errorMessage.toLowerCase().includes('invitation only')) {
        setError(
          'This platform is by invitation only. Ask your workspace administrator to invite ' +
          'you — you will get an email with a link to set your password.'
        );
      } else if (errorMessage.includes('email')) {
        setError('This email address is already registered. Please use a different email or try logging in.');
      } else if (errorMessage.includes('username')) {
        setError('This username is already taken. Please choose a different username.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 p-4">
        <div className="w-full max-w-md">
          <Card variant="elevated">
            <Card.Header
              title={success.is_first_user ? "🎉 Admin Account Created!" : "✅ Registration Successful"}
              subtitle={success.is_first_user ? "You're the first user!" : "Almost there..."}
            />
            <Card.Body>
              <div className="mb-6 p-4 rounded-lg bg-success/10 dark:bg-success/20 border border-success/30">
                <p className="text-sm text-success">{success.message}</p>
              </div>

              {!success.is_first_user && (
                <div className="mb-6 space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <p><strong>What happens next?</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>An administrator will review your registration</li>
                    <li>You'll receive an email notification once approved</li>
                    <li>After approval, you can sign in with your credentials</li>
                  </ul>
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/login')}
                className="w-full"
              >
                {success.is_first_user ? 'Go to Login' : 'Back to Login'}
              </Button>
            </Card.Body>
          </Card>
        </div>
      </div>
    );
  }

  // Registration form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-white to-secondary/10 dark:from-primary/5 dark:via-gray-900 dark:to-secondary/5 p-4">
      <div className="w-full max-w-md">
        <Card variant="elevated">
          <Card.Header
            title="Create Account"
            subtitle="For the first account on a new installation. Everyone else joins by invitation."
          />
          <Card.Body>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-error/10 dark:bg-error/20 border border-error/30">
                <p className="text-sm text-error">{error}</p>
              </div>
            )}

            {/* noValidate: validateForm below covers every rule the browser was enforcing
                (required fields, email shape, length, match) and reports them in one styled
                place. Left to the browser, an invalid email or an empty field was refused with
                a native tooltip and the page's own messages were unreachable. */}
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <Input
                id="username"
                name="username"
                type="text"
                label="Username"
                value={formData.username}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Choose a username (3+ characters)"
                autoComplete="username"
              />

              <Input
                id="email"
                name="email"
                type="email"
                label="Email Address"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="your.email@example.com"
                autoComplete="email"
              />

              <Input
                id="full_name"
                name="full_name"
                type="text"
                label="Full Name (optional)"
                value={formData.full_name}
                onChange={handleChange}
                disabled={loading}
                placeholder="Your full name"
                autoComplete="name"
              />

              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                rightIcon={showPassword ? EyeOff : Eye}
                onRightIconClick={() => setShowPassword(!showPassword)}
                rightIconLabel={showPassword ? 'Hide password' : 'Show password'}
              />

              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                label="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Re-enter your password"
                autoComplete="new-password"
              />

              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={loading || !formData.username || !formData.email || !formData.password || !formData.confirmPassword}
                className="w-full"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </Card.Body>
          <Card.Footer>
            <div className="text-center text-sm text-gray-600 dark:text-gray-400">
              Already have an account?{' '}
              <Link to="/login" className="text-primary dark:text-primary-400 hover:text-primary/80 font-medium">
                Sign in
              </Link>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
